import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connectInfiniteHits } from 'react-instantsearch-dom';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import styled from 'styled-components';
import {
  selectMyHoldingsCodes,
  selectMyHoldingsCodesError,
} from '../../store/selectors/portfolioHoldingsSelectors';
import ProductCard from '../ProductCard/ProductCard';
import { setStorageProduct } from '../../store/actions/buy/buyStorageActions';
import SellWizardModal from '../Modals/SellWizard/SellWizardModal';
import SellFromHomeWizard from '../Modals/SellWizard/SellFromHomeWizard';
import { selectProductPrices } from '../../store/selectors/productsSelector';
import {
  closeWizardContent,
  deleteWizardKey,
  setWizardContent,
} from '../../util/helpers/wizardHelpers';
import { parseEnumType } from '../../util/helpers/enumMappers';
import {
  PRODUCT_TYPE_FRACTIONAL,
  productLocationTypes,
} from '../../util/enum/api/productTypes';
import BlockSectionLoader from '../Loader/BlockSectionLoader';
import CustomCurrentRefinement from './CustomCurrentRefinement';
import {
  CHOSEN_VAULT,
  IS_MODAL_OPEN,
  IS_PRODUCT_FRACTIONAL,
  PRODUCT,
  WIZARD_DATA,
  IS_INSTA_VAULT_PRODUCT,
  QUICK_BUY_PRODUCT,
  QUICK_REGISTRATION,
  TRIGGER_ORDER_WIZARD,
  IS_PRODUCT_TRIGGER_ORDER_OPEN,
  IS_ALERT_MODAL_OPEN,
  IS_ADD_ALERT_FLOW,
  PRICE_ALERT_DEEPLINK,
  BUY_DEEPLINK,
  ACCOUNT_UID,
  GTT_TARGET_PRICE,
  GTT_TARGET_QUANTITY,
} from '../../constants/sessionStorage';
import useCheckPermissions from '../../util/hooks/useCheckPermissions';
import useTriggerOrderVerification from '../../util/hooks/useTriggerOrderVerification';
import AlgoliaLoader from '../Loader/AlgoliaLoader';
import { useSessionStorageState } from '../../util/hooks/useSessionStorageState';
import { useQuery } from '../../util/hooks/useQuery';
import { BUY_PAGE } from '../../constants/pages';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import { mediaUp, pxToRem } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import { clearBuyStorageSuccessState } from '../../store/actions/orders/orderActions';
import {
  getCurrentAccount,
  getCurrentAccountUid,
  selectAccounts,
  selectIsPriceAlertEligibleAccount,
  selectNagDashboard,
} from '../../store/selectors/accountSelectors';
import { selectAuthUser } from '../../store/selectors/userSelectors';
import { isAccountVerified } from '../../constants/nagDashboardContants';
import { accountTypeKeys } from '../../util/enum/api/accountTypes';
import { setCurrentAccount } from '../../store/actions/account/accountActions';
import { selectBankAccountAchData } from '../../store/selectors/bankAccountSelectors';
import {
  actionRequiredStatus,
  expiredVerificationStatus,
  failedVerificationStatus,
  getBankAccountStatus,
  pendingReviewStatus,
} from '../../util/helpers/bankAccountHelper';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import { BANK_ACCOUNT_LOADING } from '../../store/actions/bankAccount/bankAccountActionConstants';
import { fetchBankAccounts } from '../../store/actions/bankAccount/bankAccountActions';
// import BuyWizard from '../Modals/BuyWizard/BuyWizard';
import LoadMore from '../LoadMore/LoadMore';
import useGetPeriodicPriceTiers from '../../util/hooks/useGetPeriodicPriceTiers';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { COUNTDOWN_TIMER_INTERVAL_MS } from '../../constants/timerConstants';
import config from '../../config';
import NoProducts from './NoProducts';
import BuyFlow from '../Buy/BuyFlow';
import GTTFlow from '../GTT/GTTFlow';
import TriggerOrderBlockModal from '../GTT/TriggerOrderBlockModal';
import {
  trackGttBuyStartedBuyPage,
  ENTRY_POINTS as GTT_ENTRY_POINTS,
} from '../GTT/Common/gtmEventHelper';
import PriceAlertFlow from '../PriceAlerts/PriceAlertFlow';
import useGtmHook from '../../util/hooks/useGtmHook';
import {
  trackBuyStarted,
  ENTRY_POINTS,
} from '../PriceAlerts/Common/gtmEventHelper';
import {
  dismissPriceAlertBadge,
  fetchPriceAlerts,
} from '../../store/actions/priceAlerts/priceAlertsActions';
import { selectPriceAlerts } from '../../store/selectors/priceAlertsSelectors';
// Deep-link alert verification calls this directly (account-scoped), bypassing
// the shared `priceAlerts` redux slice — see the deep-link effects below.
import { getPriceAlertsRequest } from '../../request/priceAlertsRequests';
import { priceAlertStatuses } from '../../util/enum/api/priceAlertStatusTypes';
import { fetchProductsWithPrices } from '../../store/actions/products/productActions';

const shouldRefineByLocation = (items) => {
  const shouldFilterRefinement = !isEmpty(items);

  if (!shouldFilterRefinement) {
    return {};
  }

  const locationRefinement = items.find(
    (item) => item.attribute === 'Vaults.Location',
  );

  if (!locationRefinement) {
    return {};
  }

  return locationRefinement;
};

const filterCurrentRefinement = (products, locationRefinement) => {
  const currentRefinements =
    locationRefinement && locationRefinement.currentRefinement;
  if (isEmpty(currentRefinements)) {
    return products;
  }

  return products.filter((product) =>
    product.Locations.some((location) => {
      const currentLocation = parseEnumType(
        productLocationTypes,
        location.LocationType,
      );

      return currentRefinements.includes(currentLocation);
    }),
  );
};

const calculateProductPrice = (product, myHoldingsCodes) => {
  const products = product.Locations.filter((location) =>
    myHoldingsCodes.some(
      (holdingCode) => holdingCode.SymbolCode === location.SymbolCode,
    ),
  );

  if (isEmpty(products)) {
    return product.ProductPrice;
  }

  return products.reduce((a, b) => (a.PricePerUnit > b.PricePerUnit ? a : b))
    .PricePerUnit;
};

const filterMyHoldings = (
  isMyHoldingsChecked,
  products,
  myHoldingsCodes,
  locationRefinement,
) => {
  if (isMyHoldingsChecked && !isEmpty(myHoldingsCodes)) {
    const myHoldingProducts = products
      .filter((product) => {
        const currentRefinements =
          locationRefinement && locationRefinement.currentRefinement;

        if (isEmpty(currentRefinements)) {
          const hasProductCode = myHoldingsCodes.some(
            (holdingCode) => holdingCode.ProductCode === product.ProductCode,
          );
          return hasProductCode;
        }

        const productLocations = product.Locations.filter((location) => {
          const currentLocation = parseEnumType(
            productLocationTypes,
            location.LocationType,
          );

          return currentRefinements.includes(currentLocation);
        });
        return productLocations.some((location) =>
          myHoldingsCodes.some(
            (holdingCode) => holdingCode.SymbolCode === location.SymbolCode,
          ),
        );
      })
      .map((product) => ({
        ...product,
        ProductPrice: calculateProductPrice(product, myHoldingsCodes),
      }));
    return myHoldingProducts;
  }

  return products;
};

const InfiniteHitsItem = styled.li`
  &:not(:last-child) {
    margin-bottom: ${pxToRem(16)};
    height: 100%;
  }
`;

const ProductList = styled.ul`
  ${({ $grid }) =>
    $grid &&
    `
    ${mediaUp(variables.breakpoints.bpXl)} {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: ${pxToRem(16)};

      > li {
        min-width: 0;
        margin-bottom: 0;
        display: flex;
        flex-direction: column;
      }
    }
  `}
`;

const InfiniteHitsWrap = styled.div`
  min-height: ${pxToRem(200)};
`;

export const ShowMoreWrap = styled.div`
  margin-top: ${pxToRem(32)};
`;

const InfiniteHitsWithScrollToTop = ({
  hits,
  isMyHoldingsChecked,
  priceTiersClickHandler,
  page,
  side,
  products,
  setProducts,
  isLoading,
  isLockedPrice,
}) => {
  const dispatch = useDispatch();
  const [isSFHWizardOpen, setIsSFHWizardOpen] = useState(false);
  const { ecommerceGtmEvent } = useGtmHook();
  const [isModalOpen, setIsModalOpen] = useSessionStorageState(
    IS_MODAL_OPEN,
    false,
  );
  const [isAlertModalOpen, setIsAlertModalOpen] = useSessionStorageState(
    IS_ALERT_MODAL_OPEN,
    false,
  );
  const [isFractionalProduct, setIsFractionalProduct] = useSessionStorageState(
    IS_PRODUCT_FRACTIONAL,
    false,
  );
  // Persist the product-card GTT modal so a refresh reopens it on the same step
  // with the same values (mirrors the buy modal's IS_MODAL_OPEN persistence).
  const [isGttOpen, setIsGttOpenState] = useSessionStorageState(
    IS_PRODUCT_TRIGGER_ORDER_OPEN,
    false,
  );
  const setIsGttOpen = (value) => {
    setWizardContent(IS_PRODUCT_TRIGGER_ORDER_OPEN, value);
    setIsGttOpenState(value);
  };
  const {
    isTriggerOrderEntryHidden,
    isReadOnly,
    verificationVariant,
    closeVerification,
    blockIfIneligible,
  } = useTriggerOrderVerification();
  const [items, setItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 10;
  const settings = useSelector(selectSettings);
  const [isSegregated, setIsSegregated] = useState(false);
  useEffect(() => {
    if (isSegregated) {
      setIsSegregated(isSegregated);
    }
  }, [isSegregated]);
  const { brokerTheme } = config;
  const isGS = brokerTheme === 'GoldSilver';
  const [isInstaVaultProduct, setIsInstaVaultProduct] = useSessionStorageState(
    IS_INSTA_VAULT_PRODUCT,
    false,
  );

  const {
    allowAccountChangesPermission,
    displayIRAOptionsPermission,
    displayBasicAccountOptionsPermission,
  } = useCheckPermissions();

  const productPrices = useSelector(selectProductPrices);
  const productPricesByCode = useMemo(() => {
    const map = new Map();
    productPrices.forEach((product) => {
      if (product.Locations) {
        map.set(product.ProductCode, product);
      }
    });
    return map;
  }, [productPrices]);
  const priceAlerts = useSelector(selectPriceAlerts);
  const productCodesWithPriceAlerts = useMemo(
    () => new Set(priceAlerts.map((alert) => alert.ProductCode)),
    [priceAlerts],
  );
  const myHoldingsCodes = useSelector(selectMyHoldingsCodes);
  const myHoldingsCodesError = useSelector(selectMyHoldingsCodesError);
  const accountUid = useSelector(getCurrentAccountUid);
  const isPriceAlertEligibleAccount = useSelector(
    selectIsPriceAlertEligibleAccount,
  );
  const accounts = useSelector(selectAccounts);
  // Deep-link alert verification runs against a direct, account-scoped
  // getPriceAlertsRequest() call held in local state — NOT the shared
  // `priceAlerts` redux slice. That slice is REPLACE-on-success + takeLatest and
  // is concurrently fetched by the page effect and clobbered to empty by the
  // init-time default-account load (userSaga `setDefaultAccount`), so a one-shot
  // read of it races and can miss a valid alert. The direct request is immune.
  // Each ref holds the last `${accountUid}::${priceAlertUid}` key fetched, so the
  // request fires once per key (not once per render).
  const alertDeepLinkFetchedKeyRef = useRef(null);
  const buyDeepLinkFetchedKeyRef = useRef(null);
  // Guards the active-link, canceled-link, and verified-expired-link success
  // branches below: all three leave PRICE_ALERT_DEEPLINK in sessionStorage
  // after opening (so AlertEntry's mount effect can still read its `source` if
  // the user steps forward from the alerts list into it), so unlike the other
  // branches here they can't rely on removing it to stop re-firing when
  // `products`/other deps change identity on a later render. Each is keyed so a
  // genuinely different link still opens again.
  const activeAlertOpenedKeyRef = useRef(null);
  const cancelledAlertOpenedKeyRef = useRef(null);
  const expiredAlertOpenedKeyRef = useRef(null);
  const [alertDeepLinkData, setAlertDeepLinkData] = useState(null);
  const [buyDeepLinkData, setBuyDeepLinkData] = useState(null);
  const achBankAccounts = useSelector(selectBankAccountAchData);
  const currentAccount = useSelector(getCurrentAccount);
  const nagDashboard = useSelector(selectNagDashboard);
  const authUser = useSelector(selectAuthUser);

  const unverifiedBankAccounts = achBankAccounts?.filter((bankAccount) => {
    const verificationStatus = getBankAccountStatus(bankAccount).status;
    return [
      actionRequiredStatus,
      failedVerificationStatus,
      expiredVerificationStatus,
      pendingReviewStatus,
    ].includes(verificationStatus);
  });

  const isLoadingBankAccounts = useSelector(
    selectIsLoadingByActionType([BANK_ACCOUNT_LOADING]),
  );

  const [counterValue, setCounterValue] = useState(
    Math.floor(
      settings.PricingRefreshFrequencyMilliseconds /
        COUNTDOWN_TIMER_INTERVAL_MS,
    ),
  );
  const [unityValue, setUnityValue] = useState(false);
  const [isAddAlertFlow, setIsAddAlertFlow] = useSessionStorageState(
    IS_ADD_ALERT_FLOW,
    false,
  );

  // const quickRegistration = retrieveFromSessionStorage(QUICK_REGISTRATION);
  const quickProductCode = retrieveFromSessionStorage(QUICK_BUY_PRODUCT);

  const handleLoadMore = () => {
    setPageNumber((old) => old + 1);
  };
  useEffect(() => {
    if (accountUid) {
      dispatch(fetchBankAccounts({ accountUid }));
    }
  }, [accountUid, dispatch]);

  useEffect(() => {
    if (page === 'buy' && accountUid && isPriceAlertEligibleAccount) {
      dispatch(fetchPriceAlerts({ accountUid }));
    }
  }, [page, accountUid, isPriceAlertEligibleAccount, dispatch]);

  useEffect(() => {
    if (productPrices.length > 0 && hits.length > 0) {
      if (side === 'sell') {
        if (isMyHoldingsChecked && myHoldingsCodes.length === 0) {
          setProducts([]);
          return;
        }
        const mapProducts = hits
          .map((hit) => {
            const product = productPricesByCode.get(hit.Code);

            return !product ? null : { ...hit, ...product };
          })
          .filter(Boolean);

        const locationRefinement = shouldRefineByLocation(items);
        const myHoldingProducts = !isEmpty(myHoldingsCodes)
          ? filterMyHoldings(
              isMyHoldingsChecked,
              mapProducts,
              myHoldingsCodes,
              locationRefinement,
            )
          : mapProducts;
        const currentRefinementFiltered = filterCurrentRefinement(
          myHoldingProducts,
          locationRefinement,
        );

        setProducts(currentRefinementFiltered);
      }
      if (side === 'buy') {
        const mapProducts = hits
          .map((hit) => {
            const product = productPricesByCode.get(hit.Code);

            return !product ? null : { ...hit, ...product };
          })
          .filter(Boolean);

        const locationRefinement = shouldRefineByLocation(items);
        const myHoldingProducts = !isEmpty(myHoldingsCodes)
          ? filterMyHoldings(
              isMyHoldingsChecked,
              mapProducts,
              myHoldingsCodes,
              locationRefinement,
            )
          : mapProducts;
        const currentRefinementFiltered = filterCurrentRefinement(
          myHoldingProducts,
          locationRefinement,
        );

        setProducts(currentRefinementFiltered);
      }
    }
  }, [productPrices, isMyHoldingsChecked, hits.length, myHoldingsCodes]); // eslint-disable-line

  useEffect(() => {
    setPageNumber(1);
  }, [isMyHoldingsChecked]);

  const paginatedProducts = useMemo(
    () => products?.slice(0, pageSize * pageNumber),
    [products, pageNumber],
  );

  const handleCloseModal = () => {
    setIsModalOpen(false);
    dispatch(clearBuyStorageSuccessState());
    closeWizardContent();
    removeFromSessionStorage(QUICK_REGISTRATION);
    removeFromSessionStorage(QUICK_BUY_PRODUCT);
    removeFromSessionStorage(BUY_DEEPLINK);
  };

  const handleCloseAlertModal = () => {
    setIsAlertModalOpen(false);
    closeWizardContent();
    removeFromSessionStorage(PRICE_ALERT_DEEPLINK);
    dispatch(fetchProductsWithPrices({ accountUid, side: 'Buy' }));
  };

  const handleSelectProduct = useCallback(
    (product, vault, alertId, entryPoint, source, quantity) => {
      if (vault) {
        setWizardContent('ShortFlow', true);
        setIsSegregated(!!vault.IsSegregated);
      }

      dispatch(setStorageProduct({ product, vault }));

      setWizardContent(CHOSEN_VAULT, vault);
      setWizardContent(PRODUCT, product);
      storeInSessionStorage(WIZARD_DATA, {
        storageProduct: product,
        selectedVault: vault,
        // Attribution when the buy is launched from a triggered price alert;
        // read back in the purchase GTM event (verifyOrderEcommerceGtmHelper).
        ...(alertId && { alertId }),
        // Buy-start attribution mirrored into the purchase GTM event so
        // buy_started and buy_submitted carry the same entry_point/source.
        ...(entryPoint != null && { entryPoint }),
        ...(source !== undefined && { source }),
        // Carries a quantity over from the GTT "Buy at Current Price?"
        // hand-off (see handleGttSwitchToBuyFlow) so it isn't lost when
        // switching from the target-price flow to the regular Buy flow.
        ...(quantity && {
          orderValues: { QuantityAmount: quantity, CashAmount: 0 },
        }),
      });
      setWizardContent(IS_MODAL_OPEN, true);
      setIsModalOpen(true);

      setWizardContent(
        IS_PRODUCT_FRACTIONAL,
        product.ProductType === PRODUCT_TYPE_FRACTIONAL,
      );
      setIsFractionalProduct(product.ProductType === PRODUCT_TYPE_FRACTIONAL);

      const isInstavault =
        isGS &&
        (product.ProductCode === settings.InstavaultProductCodes[0] ||
          product.ProductCode === settings.InstavaultProductCodes[1]);
      setIsInstaVaultProduct(isInstavault);
      setWizardContent(IS_INSTA_VAULT_PRODUCT, isInstavault);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, isGS, settings],
  );

  const handleBuyNowFromAlert = (product, alertId) => {
    setIsAlertModalOpen(false);
    closeWizardContent();
    handleSelectProduct(
      product,
      undefined,
      alertId,
      ENTRY_POINTS.ACTIVE_ALERTS,
    );
  };

  const handlePriceAlert = useCallback(
    (
      product,
      vault,
      initialTab,
      forceAllAlertsList = false,
      forceAddAlertEntry = false,
    ) => {
      if (initialTab === 'Triggered') {
        dispatch(
          dismissPriceAlertBadge({
            accountUid,
            productCode: product.ProductCode,
          }),
        );
      }

      if (vault) {
        setWizardContent('ShortFlow', true);
        setIsSegregated(!!vault.IsSegregated);
      }

      dispatch(setStorageProduct({ product, vault }));

      const hasAnyPriceAlert = productCodesWithPriceAlerts.has(
        product?.ProductCode,
      );
      const addAlertFlow =
        forceAddAlertEntry ||
        (!forceAllAlertsList &&
          !hasAnyPriceAlert &&
          !product?.HasActivePriceAlerts &&
          !product?.HasNewTriggeredPriceAlerts);
      setIsAddAlertFlow(addAlertFlow);
      setWizardContent(IS_ADD_ALERT_FLOW, addAlertFlow);
      setWizardContent(CHOSEN_VAULT, vault);
      setWizardContent(PRODUCT, product);
      const existingWizardData = retrieveFromSessionStorage(WIZARD_DATA) || {};
      storeInSessionStorage(WIZARD_DATA, {
        storageProduct: product,
        selectedVault: vault,
        initialTab: initialTab || existingWizardData.initialTab || 'Active',
      });
      setWizardContent(IS_ALERT_MODAL_OPEN, true);
      setIsAlertModalOpen(true);
      setWizardContent(
        IS_PRODUCT_FRACTIONAL,
        product.ProductType === PRODUCT_TYPE_FRACTIONAL,
      );
      setIsFractionalProduct(product.ProductType === PRODUCT_TYPE_FRACTIONAL);

      const isInstavault =
        isGS &&
        (product.ProductCode === settings.InstavaultProductCodes[0] ||
          product.ProductCode === settings.InstavaultProductCodes[1]);
      setIsInstaVaultProduct(isInstavault);
      setWizardContent(IS_INSTA_VAULT_PRODUCT, isInstavault);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountUid, dispatch, isGS, settings, productCodesWithPriceAlerts],
  );

  // Open the GTT "Buy at Target Price" flow directly on the order-entry step
  // (index 1) with the given product preselected, reporting how it was entered.
  // Owned here (not inside the volatile ProductCard list item) so a
  // product-prices reload — which swaps the whole list for a loader — can't tear
  // the modal down, mirroring how BuyFlow is hosted at this level.
  const openTargetPriceOrderEntry = (product, locations, entryPoint) => {
    // This is a fresh journey for a specific (possibly different) product, but
    // it jumps straight to order-entry — skipping TargetPriceProductSelection,
    // which is normally what drops a stale target price/quantity left over
    // from a previous GTT session. Clear them here instead, or they leak into
    // this product's order-entry screen.
    removeFromSessionStorage(GTT_TARGET_PRICE);
    removeFromSessionStorage(GTT_TARGET_QUANTITY);
    storeInSessionStorage(WIZARD_DATA, {
      entryPoint,
      selectedProduct: { ...product, Locations: locations },
    });
    setWizardContent(TRIGGER_ORDER_WIZARD, 1);
    setIsGttOpen(true);
  };

  const handleBuyAtTargetPrice = (product, locations) => {
    // Ineligible accounts see the CTA but are blocked with a popup on click.
    if (blockIfIneligible()) {
      return;
    }
    trackGttBuyStartedBuyPage({
      ecommerceGtmEvent,
      productId: product?.ProductCode || product?.Code,
      productName: product?.Caption,
      weight: product?.WeightCaption,
      currentPrice: product?.ProductPrice,
      userId: authUser?.UserUid,
      accountType: parseEnumType(accountTypeKeys, currentAccount?.AccountType),
      verified: isAccountVerified(nagDashboard),
    });
    openTargetPriceOrderEntry(
      product,
      locations,
      GTT_ENTRY_POINTS.PRODUCT_CARD,
    );
  };

  // "Buy at Current Price?" hand-off from the GTT order-entry screen: clear the
  // GTT wizard step, close the GTT modal, and open the regular Buy flow with the
  // same product, (if chosen) vault, and quantity via the shared
  // handleSelectProduct. Stashes the GTT journey's entry point under its own
  // gttEntryPoint key (not Buy's own entryPoint, which has a separate
  // vocabulary and drives Buy's own GTM events — see PriceAlerts/Common/
  // gtmEventHelper's ENTRY_POINTS), so a later "Buy at Target Price?" switch
  // back to GTT can still report the journey's real origin instead of a
  // generic default, without Buy's events picking up a GTT-only value.
  const handleGttSwitchToBuyFlow = (product, vault, quantity) => {
    deleteWizardKey(TRIGGER_ORDER_WIZARD);
    setIsGttOpen(false);
    const existingWizardData = retrieveFromSessionStorage(WIZARD_DATA) || {};
    handleSelectProduct(
      product,
      vault,
      undefined,
      undefined,
      undefined,
      quantity,
    );
    if (existingWizardData.entryPoint != null) {
      storeInSessionStorage(WIZARD_DATA, {
        ...(retrieveFromSessionStorage(WIZARD_DATA) || {}),
        gttEntryPoint: existingWizardData.entryPoint,
      });
    }
  };

  // "Buy at Target Price?" hand-off from the Buy order-entry screen: close the
  // Buy modal and open the GTT flow on its order-entry step with the same
  // product (and vault/quantity) intact. Preserves the GTT journey's own
  // gttEntryPoint (stashed by handleGttSwitchToBuyFlow above on a prior round
  // trip) — not Buy's entryPoint, which may hold a Buy-vocabulary value (e.g.
  // product_direct_buy) that isn't a valid GTT entry point — falling back to
  // PRODUCT_CARD only when there's no prior GTT entry point to restore.
  const handleSwitchToTargetPrice = (product, vault, quantity) => {
    // Same permission gate as handleBuyAtTargetPrice — this link is another
    // entry point into GTT, so an ineligible user could otherwise bypass the
    // main CTA's restriction by switching over from inside the Buy flow.
    if (blockIfIneligible()) {
      return;
    }
    setIsModalOpen(false);
    setWizardContent(IS_MODAL_OPEN, false);
    const existingWizardData = retrieveFromSessionStorage(WIZARD_DATA) || {};
    storeInSessionStorage(WIZARD_DATA, {
      entryPoint:
        existingWizardData.gttEntryPoint ?? GTT_ENTRY_POINTS.PRODUCT_CARD,
      selectedProduct: product,
      ...(vault && vault.LocationType !== undefined
        ? { selectedVault: vault }
        : {}),
      ...(quantity ? { orderValues: { QuantityAmount: quantity } } : {}),
    });
    setWizardContent(TRIGGER_ORDER_WIZARD, 1);
    setIsGttOpen(true);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  //   [
  //     dispatch,
  //     // ecommerceGtmEvent,
  //     isGS,
  //     page,
  //     setIsFractionalProduct,
  //     setIsInstaVaultProduct,
  //     setIsModalOpen,
  //     settings.InstavaultProductCodes,
  //   ],
  // );

  // Deep-link entry: an external email link of the form
  // `/buy?modal=priceAlert&productCode=<ProductCode>` auto-opens the Price Alert
  // flow for the named product. We resolve the product against the full redux
  // catalog (`productPricesByCode`) rather than the paginated Algolia `hits`, so
  // pagination/active refinements don't hide it. Fires once, then strips the
  // params so the modal doesn't re-open on close or refresh.
  // Every hit on this path is, by construction, an email link (there's no other
  // way to reach `modal=priceAlert`), so record that in PRICE_ALERT_DEEPLINK —
  // AlertEntry's price_alert_started effect reads it and otherwise falls back
  // to 'my_alerts', mis-attributing this email-originated open.
  const history = useHistory();
  const { modal, productCode } = useQuery();
  const didOpenFromLinkRef = useRef(false);
  useEffect(() => {
    if (didOpenFromLinkRef.current) return;
    if (page !== 'buy' || modal !== 'priceAlert' || !productCode) return;
    // Wait for the catalog to load before attempting to resolve the product.
    if (productPricesByCode.size === 0) return;

    const product = productPricesByCode.get(productCode);
    // Product genuinely not in the Buy catalog (delisted/out of stock/bad code):
    // no-op safely rather than opening an empty modal.
    if (!product) return;

    didOpenFromLinkRef.current = true;
    storeInSessionStorage(PRICE_ALERT_DEEPLINK, {
      productCode,
      source: 'email',
    });
    handlePriceAlert(product, undefined);
    history.replace(BUY_PAGE);
  }, [
    page,
    modal,
    productCode,
    productPricesByCode,
    handlePriceAlert,
    history,
  ]);

  const periodicCallback = useCallback((epoch) => {
    if (epoch?.data) {
      const [counter, unity] = epoch.data;
      setCounterValue((prevCounter) => prevCounter - prevCounter + counter);
      setUnityValue(unity);
    }
  }, []);

  const wizardData = retrieveFromSessionStorage(WIZARD_DATA);

  useGetPeriodicPriceTiers(
    'priceTiersIHWSTT',
    currentAccount,
    parseEnumType(
      productLocationTypes,
      wizardData?.selectedVault?.LocationType,
    ),
    wizardData?.selectedVault?.SymbolCode,
    'Sell',
    dispatch,
    page === 'sell' &&
      isModalOpen &&
      isFractionalProduct &&
      !isInstaVaultProduct,
    settings?.PricingRefreshFrequencyMilliseconds,
    true,
    page === 'sell' &&
      isModalOpen &&
      isFractionalProduct &&
      !isInstaVaultProduct
      ? periodicCallback
      : null,
  );

  useEffect(() => {
    if (products && products?.length > 0 && quickProductCode) {
      const selectedProduct = products.find(
        (product) => product?.Code === quickProductCode,
      );
      if (selectedProduct) {
        const selectedLocation = selectedProduct?.Locations?.find(
          (location) => location.LocationType === 12,
        );
        handleSelectProduct(selectedProduct, selectedLocation);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, quickProductCode]);

  // Price Alert notification deep link (email/SMS): open My Alerts > Active for the alert's product
  // once products have loaded (mirrors the quickProductCode effect above).
  const priceAlertDeepLink = retrieveFromSessionStorage(PRICE_ALERT_DEEPLINK);

  useEffect(() => {
    if (
      page === 'buy' &&
      products &&
      products?.length > 0 &&
      priceAlertDeepLink?.productCode &&
      // `status` is only ever set by storePriceAlertDeepLinkFromSearch (the
      // openAlerts=... deep link). The modal=priceAlert handler above writes
      // PRICE_ALERT_DEEPLINK too, but only to carry `source` for
      // AlertEntry's price_alert_started effect — it already opened the
      // modal itself, so this effect must not also act on it.
      priceAlertDeepLink?.status
    ) {
      // Switch to the account the alert belongs to before opening, so the modal
      // loads that account's alerts (fetchPriceAlerts keys off the current
      // account). Then bail out; the account change refetches products and this
      // effect re-runs to open the modal for the correct account.
      if (
        priceAlertDeepLink.accountUid &&
        priceAlertDeepLink.accountUid !== accountUid
      ) {
        // Wait for the accounts list before resolving the target account.
        if (!accounts || accounts.length === 0) return;
        const targetAccount = accounts.find(
          (account) => account.AccountUid === priceAlertDeepLink.accountUid,
        );
        if (targetAccount) {
          storeInSessionStorage(ACCOUNT_UID, targetAccount.AccountUid);
          dispatch(setCurrentAccount(targetAccount));
          return;
        }
        // accountUid isn't one of the user's accounts (typo / forwarded email /
        // revoked access): do NOT open for the wrong account — discard the link.
        removeFromSessionStorage(PRICE_ALERT_DEEPLINK);
        return;
      }

      // productCode guard: the product must exist in the current catalog.
      const selectedProduct = products.find(
        (product) => product?.Code === priceAlertDeepLink.productCode,
      );
      if (!selectedProduct) {
        removeFromSessionStorage(PRICE_ALERT_DEEPLINK);
        return;
      }

      const { priceAlertUid, status } = priceAlertDeepLink;

      // Canceled links are special: "canceling" an alert deletes it
      // server-side (see onDeleteAlert in AllAlertsList), so it will NOT be
      // returned by getPriceAlertsRequest and can never satisfy the list-based
      // verification below. A canceled link always opens a fresh Alert Entry
      // (add-alert) form for the product, so the meaningful guards are the
      // productCode (validated above against the catalog) and the accountUid
      // (validated above against the user's accounts). Skip the uid/list
      // verification for `cancelled` ONLY — every other status is unchanged.
      if (status === 'cancelled') {
        // One-shot per product+account: PRICE_ALERT_DEEPLINK is deliberately
        // NOT removed below (see expiredAlertOpenedKeyRef above for why), so
        // this guard stops a later re-render from re-opening the form.
        const cancelledKey = `${accountUid}::${priceAlertDeepLink.productCode}`;
        if (cancelledAlertOpenedKeyRef.current === cancelledKey) return;
        cancelledAlertOpenedKeyRef.current = cancelledKey;

        const cancelledLocation = selectedProduct?.Locations?.find(
          (location) => location.LocationType === 12,
        );
        handlePriceAlert(
          selectedProduct,
          cancelledLocation,
          undefined,
          false,
          true,
        );
        return;
      }

      const selectedLocation = selectedProduct?.Locations?.find(
        (location) => location.LocationType === 12,
      );

      // Active links open the My Alerts list on the Active tab. The alert the
      // link points at may since have moved from Active to Triggered/Expired, or
      // been deleted entirely (no uid match). In every one of those cases we
      // still open the Active Alerts modal for the product rather than silently
      // dropping the link — the user always lands on a meaningful destination and
      // can see whatever active alerts remain. So no uid/status verification is
      // needed here: the productCode (validated above against the catalog) and
      // accountUid (validated above against the user's accounts) are enough.
      if (status !== 'expired') {
        // One-shot per product+account: PRICE_ALERT_DEEPLINK is deliberately
        // NOT removed below (see activeAlertOpenedKeyRef above for why), so
        // this guard stops a later re-render from re-opening the list. Without
        // it, removing the deep link here would race AlertEntry's mount effect
        // if the user steps forward from this list into "Add Another Alert" —
        // AlertEntry would then always read a missing source and mis-attribute
        // price_alert_started to 'my_alerts' instead of the link's channel
        // (e.g. 'sms').
        const activeKey = `${accountUid}::${priceAlertDeepLink.productCode}`;
        if (activeAlertOpenedKeyRef.current === activeKey) return;
        activeAlertOpenedKeyRef.current = activeKey;
        handlePriceAlert(selectedProduct, selectedLocation, 'Active', true);
        return;
      }

      // Expired links open a fresh Alert Entry (add-alert) form. Verify the
      // alert's server-side status via a direct, account-scoped request held in
      // local state (see the ref/state declarations above for why we bypass the
      // shared redux slice). Fire the request once per account+uid key, then
      // verify against the returned list once it lands for THIS key.
      if (!priceAlertUid) {
        removeFromSessionStorage(PRICE_ALERT_DEEPLINK);
        return;
      }
      const alertKey = `${accountUid}::${priceAlertUid}`;
      if (alertDeepLinkFetchedKeyRef.current !== alertKey) {
        alertDeepLinkFetchedKeyRef.current = alertKey;
        getPriceAlertsRequest(accountUid)
          .then((res) =>
            setAlertDeepLinkData({ key: alertKey, list: res?.data || [] }),
          )
          .catch(() => setAlertDeepLinkData({ key: alertKey, list: [] }));
        return;
      }
      if (!alertDeepLinkData || alertDeepLinkData.key !== alertKey) return;

      const verifiedAlert = alertDeepLinkData.list.find(
        (alert) => alert.PriceAlertUid === priceAlertUid,
      );

      // A stale expired link — the alert no longer exists (e.g. it was deleted
      // via Clear All on the Expired tab). Rather than silently dropping the
      // link, open the Active Alerts list for the product so the user still
      // lands on a meaningful destination, consistent with the active-link
      // fallback above.
      if (!verifiedAlert) {
        handlePriceAlert(selectedProduct, selectedLocation, 'Active', true);
        removeFromSessionStorage(PRICE_ALERT_DEEPLINK);
        return;
      }

      // The verified alert must belong to the product named in the link, and be
      // neither Active nor Triggered (i.e. Expired/Canceled) before we open the
      // Alert Entry form.
      if (
        verifiedAlert.ProductCode !== priceAlertDeepLink.productCode ||
        verifiedAlert.PriceAlertStatus === priceAlertStatuses.Active ||
        verifiedAlert.PriceAlertStatus === priceAlertStatuses.Triggered
      ) {
        removeFromSessionStorage(PRICE_ALERT_DEEPLINK);
        return;
      }

      // One-shot per alertKey: PRICE_ALERT_DEEPLINK is deliberately NOT
      // removed below (handlePriceAlert mounts AlertEntry on a later render,
      // and its own mount effect reads PRICE_ALERT_DEEPLINK for the
      // price_alert_started `source` after this effect returns — removing it
      // now would race that read and always lose), so without this guard a
      // later re-render (e.g. `products` getting a new reference from a price
      // refresh) would re-run this branch and re-open the form. It's still
      // cleaned up once the user closes the alert modal (see
      // handleCloseAlertModal above).
      if (expiredAlertOpenedKeyRef.current === alertKey) return;
      expiredAlertOpenedKeyRef.current = alertKey;
      handlePriceAlert(
        selectedProduct,
        selectedLocation,
        undefined,
        false,
        true,
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    products,
    priceAlertDeepLink?.productCode,
    accountUid,
    accounts,
    alertDeepLinkData,
  ]);

  // Price Alert BUY deep link (email/SMS): open the Buy modal for the alert's
  // product once products have loaded (mirrors the priceAlertDeepLink effect
  // above). Unlike quick-buy, we pass no vault so the user lands on the full
  // vault/location chooser step of BuyFlow.
  const buyDeepLink = retrieveFromSessionStorage(BUY_DEEPLINK);

  useEffect(() => {
    if (
      page === 'buy' &&
      products &&
      products?.length > 0 &&
      buyDeepLink?.productCode
    ) {
      // Switch to the account the alert belongs to before opening the Buy modal,
      // so the purchase happens under the correct account. Then bail out and let
      // the account change re-run this effect.
      if (buyDeepLink.accountUid && buyDeepLink.accountUid !== accountUid) {
        // Wait for the accounts list before resolving the target account.
        if (!accounts || accounts.length === 0) return;
        const targetAccount = accounts.find(
          (account) => account.AccountUid === buyDeepLink.accountUid,
        );
        if (targetAccount) {
          storeInSessionStorage(ACCOUNT_UID, targetAccount.AccountUid);
          dispatch(setCurrentAccount(targetAccount));
          return;
        }
        // accountUid isn't one of the user's accounts (typo / forwarded email /
        // revoked access): do NOT open the Buy modal for the wrong account.
        removeFromSessionStorage(BUY_DEEPLINK);
        return;
      }
      // productCode guard: the product must exist in the current catalog.
      const selectedProduct = products.find(
        (product) => product?.Code === buyDeepLink.productCode,
      );
      if (!selectedProduct) {
        removeFromSessionStorage(BUY_DEEPLINK);
        return;
      }

      // priceAlertUid guard: a Buy link comes from a Triggered alert, so verify
      // the alert's server-side status is Triggered before opening the Buy modal.
      const { priceAlertUid } = buyDeepLink;
      if (!priceAlertUid) {
        removeFromSessionStorage(BUY_DEEPLINK);
        return;
      }
      // Verify via a direct, account-scoped request held in local state (see the
      // alert deep-link effect above for why we bypass the shared redux slice).
      // Fire once per account+uid key, then verify against the returned list.
      const buyKey = `${accountUid}::${priceAlertUid}`;
      if (buyDeepLinkFetchedKeyRef.current !== buyKey) {
        buyDeepLinkFetchedKeyRef.current = buyKey;
        getPriceAlertsRequest(accountUid)
          .then((res) =>
            setBuyDeepLinkData({ key: buyKey, list: res?.data || [] }),
          )
          .catch(() => setBuyDeepLinkData({ key: buyKey, list: [] }));
        return;
      }
      if (!buyDeepLinkData || buyDeepLinkData.key !== buyKey) return;

      const verifiedAlert = buyDeepLinkData.list.find(
        (alert) => alert.PriceAlertUid === priceAlertUid,
      );
      if (
        !verifiedAlert ||
        verifiedAlert.ProductCode !== buyDeepLink.productCode ||
        verifiedAlert.PriceAlertStatus !== priceAlertStatuses.Triggered
      ) {
        // Unverifiable, belongs to a different product, or no longer triggered:
        // suppress. All four link fields (accountUid, productCode, priceAlertUid,
        // PriceAlertStatus) must describe the same alert record.
        removeFromSessionStorage(BUY_DEEPLINK);
        return;
      }

      // Notification channel the link came through (email/sms/text), parsed
      // from the &source deep-link param. Absent/unrecognised -> null.
      const deepLinkSource = buyDeepLink.source ?? null;
      trackBuyStarted({
        ecommerceGtmEvent,
        alertId: verifiedAlert.PriceAlertUid,
        productName: selectedProduct?.Caption,
        entryPoint: ENTRY_POINTS.EMAIL_TEXT,
        source: deepLinkSource,
      });
      handleSelectProduct(
        selectedProduct,
        undefined,
        undefined,
        ENTRY_POINTS.EMAIL_TEXT,
        deepLinkSource,
      );
      removeFromSessionStorage(BUY_DEEPLINK);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    products,
    buyDeepLink?.productCode,
    accountUid,
    accounts,
    buyDeepLinkData,
  ]);

  const renderHits = () => (
    <>
      <CustomCurrentRefinement setItems={setItems} />
      {isEmpty(products) || isEmpty(hits) ? (
        <NoProducts side={side} />
      ) : (
        <>
          <ProductList $grid={isGS && side === 'buy'}>
            {paginatedProducts.map((product) => (
              <InfiniteHitsItem
                key={product.objectID}
                data-cy="container-product-item"
              >
                <ProductCard
                  displayIRAOptionsPermission={displayIRAOptionsPermission}
                  product={product}
                  isTestProduct={
                    product.Locations.filter((product) => product.IsTestProduct)
                      .length > 0
                  }
                  isTradingEnabled={product.IsTradingEnabled}
                  collapsable={side === 'buy'}
                  priceTiersClickHandler={priceTiersClickHandler}
                  selectProduct={handleSelectProduct}
                  onBuyAtTargetPrice={handleBuyAtTargetPrice}
                  hideBuyAtTargetPrice={isTriggerOrderEntryHidden}
                  lockBuyAtTargetPrice={isReadOnly}
                  handlePriceAlert={handlePriceAlert}
                  hasAnyPriceAlert={productCodesWithPriceAlerts.has(
                    product.ProductCode,
                  )}
                  myHoldingsCodes={myHoldingsCodes}
                  side={side}
                  isMyHoldingsChecked={isMyHoldingsChecked}
                  allowAccountChangesPermission={allowAccountChangesPermission}
                  displayBasicAccountOptionsPermission={
                    displayBasicAccountOptionsPermission
                  }
                  wizardData={wizardData}
                />
              </InfiniteHitsItem>
            ))}
          </ProductList>
          <ShowMoreWrap>
            <LoadMore onLoadClick={handleLoadMore} />
          </ShowMoreWrap>
        </>
      )}

      {page === 'buy'
        ? isModalOpen && (
            // <BuyWizard
            //   isModalOpen={isModalOpen}
            //   handleCloseModal={handleCloseModal}
            //   unverifiedBankAccounts={unverifiedBankAccounts}
            //   isLockedPrice={isLockedPrice}
            //   isSegregated={isSegregated}
            // />
            <BuyFlow
              isModalOpen={isModalOpen}
              handleCloseModal={handleCloseModal}
              unverifiedBankAccounts={unverifiedBankAccounts}
              isLockedPrice={isLockedPrice}
              isSegregated={isSegregated}
              isInstaVaultProduct={isInstaVaultProduct}
              onSwitchToTargetPrice={
                settings?.IsTriggerOrderActive && !isTriggerOrderEntryHidden
                  ? handleSwitchToTargetPrice
                  : undefined
              }
            />
          )
        : isModalOpen && (
            <SellWizardModal
              isModalOpen={isModalOpen}
              handleCloseModal={handleCloseModal}
              isFractionalProduct={isFractionalProduct}
              isInstaVaultProduct={isInstaVaultProduct}
              isSegregated={isSegregated}
              isLockedPrice={isLockedPrice}
              counter={counterValue}
              unity={unityValue}
              onOpenSFHWizard={() => {
                setIsSFHWizardOpen(true);
              }}
            />
          )}
      <SellFromHomeWizard
        isOpen={isSFHWizardOpen}
        onClose={() => setIsSFHWizardOpen(false)}
        fromSellWizard
      />
      {page === 'buy' && isAlertModalOpen && (
        <PriceAlertFlow
          isModalOpen={isAlertModalOpen}
          handleCloseModal={handleCloseAlertModal}
          isFractionalProduct={isFractionalProduct}
          isInstaVaultProduct={isInstaVaultProduct}
          isSegregated={isSegregated}
          isLockedPrice={isLockedPrice}
          isAddAlertFlow={isAddAlertFlow}
          onBuyNow={handleBuyNowFromAlert}
        />
      )}
    </>
  );

  const listLoadingRender = () => {
    if (products === null || isLoadingBankAccounts) {
      return <BlockSectionLoader isLoading />;
    }

    return <div>{renderHits()}</div>;
  };

  return (
    <>
      {isLoading && products === null ? (
        <BlockSectionLoader isLoading />
      ) : (
        <InfiniteHitsWrap>
          <AlgoliaLoader>
            {myHoldingsCodesError && <span>{myHoldingsCodesError}</span>}
            {listLoadingRender()}
          </AlgoliaLoader>
        </InfiniteHitsWrap>
      )}
      {/* Rendered outside the isLoading swap (and the product list) so a
          product-prices reload can't unmount the open GTT modal. */}
      {isGttOpen && (
        <GTTFlow
          isModalOpen={isGttOpen}
          handleCloseModal={() => setIsGttOpen(false)}
          onSwitchToBuyFlow={handleGttSwitchToBuyFlow}
        />
      )}
      <TriggerOrderBlockModal
        variant={verificationVariant}
        close={closeVerification}
      />
    </>
  );
};

InfiniteHitsWithScrollToTop.propTypes = {
  hits: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isMyHoldingsChecked: PropTypes.bool,
  priceTiersClickHandler: PropTypes.func,
  page: PropTypes.string,
  side: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.shape({})),
  setProducts: PropTypes.func,
  isLoading: PropTypes.bool,
  isLockedPrice: PropTypes.bool,
};

export default connectInfiniteHits(InfiniteHitsWithScrollToTop);
